import React from "react";
import { makeStyles } from "@material-ui/core/styles";

import { T } from "../constants";
import { useUi } from "../ui";
import ModalCard from "../components/ModalCard";
import Plate from "../components/Plate";
import PhotoCaptureBlock from "../dashboard/components/PhotoCaptureBlock";
import VehiclePhotoGallery from "../dashboard/components/VehiclePhotoGallery";
import useHandoverPhotos from "../dashboard/useHandoverPhotos";
import { VEHICLE_PHOTO_MODES } from "../../../lib/conciergeFeature";

const useStyles = makeStyles({
  head: { display: "flex", alignItems: "center", gap: 10, marginBottom: 12 },
  hint: { fontSize: 12, color: T.mutedFg, marginBottom: 12 },
});

/*
 * The car is in the runner's hands and nobody is waiting on it yet - the one
 * unhurried moment to photograph its condition. The same set the handover asks
 * for, shot early: an angle taken here arrives at the door already done.
 *
 * There is no way past the set in here: Skip is on the queue row, so opening
 * this modal is the runner saying they are taking the photos.
 */
const COLLECTION_PHOTO_HINTS = {
  noBooking:
    "This car has no booking on file, so collection photos cannot be saved against it.",
  hydrating: "Loading any photos already taken for this car...",
  error: "A photo did not upload. Retry it, or go without it.",
  busy: "Uploading - keep this screen open until the photos finish.",
  allDone: "All angles captured. The handover will not ask again.",
  idle:
    "Shoot the car before you drive it - the guest is not waiting on you yet. Skip is on the queue row.",
};

const REQUIRED_COLLECTION_PHOTO_HINTS = {
  ...COLLECTION_PHOTO_HINTS,
  error: "A photo did not upload. Retry it before continuing to the guest.",
  idle: "Shoot every angle before driving back to the guest.",
};

export default function CollectionPhotoModal({
  job,
  facilityId,
  advancing,
  onGo,
  onClose,
  vehiclePhotoMode = VEHICLE_PHOTO_MODES.OPTIONAL,
}) {
  const s = useStyles();
  const ui = useUi();
  const photosRequired = vehiclePhotoMode === VEHICLE_PHOTO_MODES.REQUIRED;

  const collection = useHandoverPhotos({
    bookingId: job.bookingId,
    facilityId,
    valetSessionId: job.valetSessionId,
  });

  return (
    <ModalCard
      title="Collection photos"
      subtitle={job.guestName}
      onClose={onClose}
      width={520}
      footerRight={
        <>
          <button type="button" className={ui.mutedBtn} onClick={onClose}>
            Back
          </button>
          <button
            type="button"
            className={ui.primaryBtn}
            disabled={advancing || collection.anyBusy || !collection.allDone}
            onClick={onGo}
          >
            {advancing
              ? "Going..."
              : collection.anyBusy
              ? "Uploading photos..."
              : "En route to guest"}
          </button>
        </>
      }
    >
      <div className={s.head}>
        <Plate size={13}>{job.plate}</Plate>
        <span>{job.vehicle}</span>
      </div>

      <div className={s.hint}>
        {photosRequired
          ? "Condition photos are required before starting the trip back to the guest."
          : "Condition photos for the trip back. Back out to the queue to skip them - the handover will ask again at the guest."}
      </div>

      <PhotoCaptureBlock
        eyebrow="Collection photos"
        hints={
          photosRequired
            ? REQUIRED_COLLECTION_PHOTO_HINTS
            : COLLECTION_PHOTO_HINTS
        }
        bookingId={job.bookingId}
        photos={collection.photos}
        onCapture={collection.capture}
        onRetry={collection.retry}
        hydrating={collection.hydrating}
        anyBusy={collection.anyBusy}
        anyError={collection.anyError}
        required={photosRequired}
        framed={false}
      />
    </ModalCard>
  );
}
